import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { PopoverMenu, PopoverMenuItem, usePopoverMenu } from '@frontend/design-system';
import { getNewListAndOrderIdMap } from '../../../../hooks';
import { CalendarEntityType } from '../../../../stores';
import { DRAG_PENDO_EVENT_ENTRY_POINTS } from '../../constants';
import { ColumnEntity } from '../../types';
import { useAddNewOrderToQueue, UseAddNewOrderToQueueProps, usePendoTrackingForDragDrop } from '../hooks';

export type ColumnHeaderPopoverMenuProps = UseAddNewOrderToQueueProps & {
  entityId: string;
  list: ColumnEntity[];
  index: number;
  popoverMenuProps: ReturnType<typeof usePopoverMenu>;
  calendarEntityType: CalendarEntityType;
};

export const ColumnHeaderPopoverMenu = ({
  popoverMenuProps,
  index,
  list,
  entityId,
  calendarEntityType,
  ...rest
}: ColumnHeaderPopoverMenuProps) => {
  const { t } = useTranslation('schedule');
  const { getMenuProps, getItemProps } = popoverMenuProps;

  const { addNewOrderToQueue } = useAddNewOrderToQueue({ ...rest, calendarEntityType });

  const trackReorderStartEvent = usePendoTrackingForDragDrop({
    eventName: 'schedule_column_reordered',
    entryPoint: DRAG_PENDO_EVENT_ENTRY_POINTS.menu,
  });

  const moveColumn = (direction: 'left' | 'right') => {
    const entityIndex = list.findIndex((entity) => entity.id === entityId);
    const targetIndex = direction === 'left' ? entityIndex - 1 : entityIndex + 1;
    if (entityIndex === -1 || targetIndex < 0 || targetIndex >= list.length) {
      return;
    }
    const { newList, newOrderIdMap } = getNewListAndOrderIdMap(list, entityIndex, targetIndex);
    addNewOrderToQueue(newOrderIdMap, newList);
    trackReorderStartEvent();
  };

  const isFirstItem = index === 0;
  const isLastItem = index === list.length - 1;

  return (
    <>
      <PopoverMenu {...getMenuProps()}>
        <PopoverMenuItem
          {...getItemProps({
            index: 1,
          })}
          onClick={() => moveColumn('right')}
          Icon={() => <Icon name='arrow-right-small' />}
          disabled={isLastItem}
        >
          {t('Move to the Right')}
        </PopoverMenuItem>
        <PopoverMenuItem
          {...getItemProps({
            index: 2,
          })}
          onClick={() => moveColumn('left')}
          Icon={() => <Icon name='back-small' />}
          disabled={isFirstItem}
        >
          {t('Move to the Left')}
        </PopoverMenuItem>
      </PopoverMenu>
    </>
  );
};

ColumnHeaderPopoverMenu.displayName = 'ColumnHeaderPopoverMenu';
